import * as B from '@blueprintjs/core';
import React from 'react';

import {OptionCard} from 'app/components/AgentExplorer/OptionCard';
import {
  AGENT_CUSTOM_PROMPT_KEY,
  AGENT_CUSTOM_PROMPT_USE_CASE,
  AGENT_USE_CASES,
  getAgentTypePlaceholder,
} from 'app/components/AgentExplorer/PromptView/promptConfigs';
import {ExploreRunAgentType} from 'app/components/AgentExplorer/types';
import {UseCaseCard} from 'app/components/AgentExplorer/UseCaseCard';

import cs from './styles.module.scss';
import {RunPlanDraft} from './types';

const MAX_SEGMENTATION_CONCEPTS = 5;

const AGENT_MODE_OPTIONS: {
  value: ExploreRunAgentType;
  icon: B.IconName;
  label: string;
  description: string;
}[] = [
  {
    value: 'describe',
    icon: 'manually-entered-data',
    label: 'Written analysis',
    description: 'Prompt the Agent in plain language. Returns written observations.',
  },
  {
    value: 'identify',
    icon: 'polygon-filter',
    label: 'Feature segmentation',
    description: 'Pick features to outline. Returns a shape for each one found.',
  },
];

export const WhatToLookForStep: React.FC<{
  draft: RunPlanDraft;
  setDraft: (patch: Partial<RunPlanDraft>) => void;
  canUseCustomPrompt: boolean;
}> = ({draft, setDraft, canUseCustomPrompt}) => {
  const compareMode = draft.sceneMode === 'compare';
  const isCustomSelected = draft.promptKey === AGENT_CUSTOM_PROMPT_KEY;

  const handleConceptsChange = (values: React.ReactNode[]) => {
    const concepts = [...new Set(values.map((v) => String(v).trim()).filter(Boolean))].slice(
      0,
      MAX_SEGMENTATION_CONCEPTS
    );
    setDraft({customPromptConcepts: concepts});
  };

  return (
    <div className={cs.step}>
      <div className={cs.useCases}>
        {[...AGENT_USE_CASES, ...(canUseCustomPrompt ? [AGENT_CUSTOM_PROMPT_USE_CASE] : [])].map(
          (useCase) => (
            <UseCaseCard
              key={useCase.key}
              useCase={useCase}
              compareMode={compareMode}
              selected={draft.promptKey === useCase.key}
              disabled={!useCase.supportsBatch}
              tag={useCase.supportsBatch ? undefined : 'Coming soon'}
              onSelect={(key) => setDraft({promptKey: key})}
            />
          )
        )}
      </div>

      {isCustomSelected && (
        <div className={cs.customPromptSection}>
          <B.FormGroup label="How should the Agent work?" className={cs.agentModeGroup}>
            <div className={cs.optionGrid}>
              {AGENT_MODE_OPTIONS.map((option) => (
                <OptionCard
                  key={option.value}
                  icon={option.icon}
                  title={option.label}
                  description={option.description}
                  selected={draft.customPromptAgentType === option.value}
                  onSelect={() => setDraft({customPromptAgentType: option.value})}
                />
              ))}
            </div>
          </B.FormGroup>
          {draft.customPromptAgentType === 'describe' ? (
            <B.TextArea
              className={cs.promptTextArea}
              value={draft.customPromptText}
              onChange={(e) => setDraft({customPromptText: e.currentTarget.value})}
              placeholder={getAgentTypePlaceholder('describe', compareMode)}
              fill
            />
          ) : (
            <B.FormGroup
              helperText={`Up to ${MAX_SEGMENTATION_CONCEPTS} features. Press Enter after each one.`}
              className={cs.conceptsGroup}
            >
              <B.TagInput
                values={draft.customPromptConcepts}
                onChange={handleConceptsChange}
                placeholder="What should the Agent outline? e.g. solar panels, pools"
                addOnBlur
                fill
                inputProps={{'aria-label': 'Features to outline'}}
              />
            </B.FormGroup>
          )}
        </div>
      )}
    </div>
  );
};
